import React, { useMemo, useState, useEffect } from "react";
import { Question } from "../../../../Entities/Question";
import { Typography, Radio, Empty, Collapse, Button, Space, Tag } from "antd";
import { ExpandOutlined, CompressOutlined } from "@ant-design/icons";
import JobApplicationinterviewRoundsDetailsQuestionItem from "./JobApplicationinterviewRoundsDetailsQuestionItem";

const { Panel } = Collapse;

interface JobApplicationinterviewRoundsDetailsQuestionsProps {
  roundQuestions: Question[];
  isRoundPending: boolean;
  questionFilter: "all" | "qualified" | "unqualified" | "pending";
  setQuestionFilter?: (filter: "all" | "qualified" | "unqualified" | "pending") => void;
}

const JobApplicationinterviewRoundsDetailsQuestions: React.FC<
  JobApplicationinterviewRoundsDetailsQuestionsProps
> = ({ roundQuestions, isRoundPending, questionFilter, setQuestionFilter }) => {
  const filteredQuestions = useMemo(() => {
    if (questionFilter === "all") {
      return roundQuestions;
    }
    return roundQuestions.filter((question) => {
      if (questionFilter === "qualified") {
        return question.evaluation_status === "qualified";
      }
      if (questionFilter === "unqualified") {
        return question.evaluation_status === "unqualified";
      }
      if (questionFilter === "pending") {
        // Pending = questions without evaluation_status (matches useAssessmentStatistics logic)
        return !question.evaluation_status;
      }
      return true;
    });
  }, [roundQuestions, questionFilter]);

  // Calculate counts for each filter - matching logic from useAssessmentStatistics
  const filterCounts = useMemo(() => {
    const total = roundQuestions.length;
    const qualified = roundQuestions.filter((q) => q.evaluation_status === "qualified").length;
    const unqualified = roundQuestions.filter((q) => q.evaluation_status === "unqualified").length;
    // Pending = questions without evaluation_status (matches useAssessmentStatistics logic)
    const pending = roundQuestions.filter((q) => !q.evaluation_status).length;
    
    return {
      all: total,
      qualified,
      unqualified,
      pending,
    };
  }, [roundQuestions]);

  // State to manage which accordions are open
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  // Initialize with first question open by default
  useEffect(() => {
    if (filteredQuestions.length > 0) {
      const firstQuestionId = filteredQuestions[0]._id || `question-0`;
      setActiveKeys([firstQuestionId]);
    } else {
      setActiveKeys([]);
    }
  }, [filteredQuestions]);

  const handleFilterChange = (e: any) => {
    const value = e.target.value as "all" | "qualified" | "unqualified" | "pending";
    setQuestionFilter?.(value);
  };

  const handleExpandAll = () => {
    const allKeys = filteredQuestions.map((q, index) => q._id || `question-${index}`);
    setActiveKeys(allKeys);
  };

  const handleCollapseAll = () => {
    setActiveKeys([]);
  };

  const handlePanelChange = (keys: string | string[]) => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  const isAllExpanded = activeKeys.length === filteredQuestions.length;
  const isAllCollapsed = activeKeys.length === 0;

  return (
    <div className="job-application-interview-rounds-details-questions-container">
      <div className="job-application-interview-rounds-details-section-header">
        <Typography.Text strong>Questions</Typography.Text>
        <Space>
          {setQuestionFilter && (
            <Radio.Group
              value={questionFilter}
              onChange={handleFilterChange}
              buttonStyle="solid"
              size="small"
              className="job-application-interview-rounds-details-questions-filter"
            >
              <Radio.Button value="all">All ({filterCounts.all})</Radio.Button>
              <Radio.Button value="qualified">Qualified ({filterCounts.qualified})</Radio.Button>
              <Radio.Button value="unqualified">Unqualified ({filterCounts.unqualified})</Radio.Button>
              {/* <Radio.Button value="pending">Pending </Radio.Button> */}
            </Radio.Group>
          )}
          {filteredQuestions.length > 0 && (
            <Space size="small">
              <Button
                type="text"
                size="small"
                icon={<ExpandOutlined />}
                onClick={handleExpandAll}
                disabled={isAllExpanded}
              >
                Expand All
              </Button>
              <Button
                type="text"
                size="small"
                icon={<CompressOutlined />}
                onClick={handleCollapseAll}
                disabled={isAllCollapsed}
              >
                Collapse All
              </Button>
            </Space>
          )}
        </Space>
      </div>

      <div className="job-application-interview-rounds-details-questions-content">
        {filteredQuestions.length === 0 ? (
          <div style={{ padding: "32px 16px", textAlign: "center" }}>
            <Empty description={`No ${questionFilter === "all" ? "" : questionFilter} questions found`} />
          </div>
        ) : (
          <Collapse
            activeKey={activeKeys}
            onChange={handlePanelChange}
            accordion={false}
            className="interview-rounds-questions-accordion"
          >
            {filteredQuestions.map((question, index) => {
              const questionId = question._id || `question-${index}`;
              const evaluationStatus = question?.evaluation_status || "pending";
              const tagColor =
                evaluationStatus === "qualified"
                  ? "green"
                  : evaluationStatus === "unqualified"
                  ? "red"
                  : "blue";

              return (
                <Panel
                  key={questionId}
                  header={
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Typography.Text style={{ whiteSpace: "normal", wordBreak: "break-word", display: "block" }}>
                          {question.description || `Question ${index + 1}`}
                        </Typography.Text>
                        <span style={{ color: "#8c8c8c", fontSize: 12, fontWeight: "normal", marginTop: 4, display: "block" }}>
                          ({question.type.toUpperCase()})
                        </span>
                      </div>
                      {!isRoundPending && (
                        <Tag style={{ fontSize: 10, flexShrink: 0 }} color={tagColor}>
                          {evaluationStatus.toUpperCase()}
                        </Tag>
                      )}
                    </div>
                  }
                >
                  <JobApplicationinterviewRoundsDetailsQuestionItem
                    question={question}
                    questionNo={index + 1}
                    isRoundPending={isRoundPending}
                  />
                </Panel>
              );
            })}
          </Collapse>
        )}
      </div>
    </div>
  );
};

export default JobApplicationinterviewRoundsDetailsQuestions;
